<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import BuhotextDeviceCard from './BuhotextDeviceCard.vue';
import BuhotextConnectDeviceDialog from './BuhotextConnectDeviceDialog.vue';

const emit = defineEmits(['back']);
const { t } = useI18n();
const store = useStore();

const [showConnectDialog, toggleConnectDialog] = useToggle();
const devices = useMapGetter('buhotextCampaigns/getDevices');
const uiFlags = useMapGetter('buhotextCampaigns/getUIFlags');

const isFetchingDevices = computed(() => uiFlags.value.isFetchingDevices);
const hasNoDevices = computed(
  () => devices.value.length === 0 && !isFetchingDevices.value
);

const handleConnectDevice = () => {
  toggleConnectDialog(true);
};

const handleRefresh = async () => {
  try {
    await store.dispatch('buhotextCampaigns/fetchDevices');
    useAlert(t('CAMPAIGN.BUHOTEXT.DEVICES.REFRESH_SUCCESS'));
  } catch (error) {
    useAlert(t('CAMPAIGN.BUHOTEXT.DEVICES.REFRESH_ERROR'), 'error');
  }
};

onMounted(() => {
  store.dispatch('buhotextCampaigns/fetchDevices');
});
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-3">
        <Button icon="i-lucide-arrow-left" slate xs @click="emit('back')" />
        <div>
          <h2 class="text-xl font-semibold text-n-slate-12">
            {{ t('CAMPAIGN.BUHOTEXT.DEVICES.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ t('CAMPAIGN.BUHOTEXT.DEVICES.SUBTITLE') }}
          </p>
        </div>
      </div>

      <div class="flex gap-2">
        <Button
          icon="i-lucide-refresh-cw"
          slate
          :loading="isFetchingDevices"
          @click="handleRefresh"
        />
        <Button
          :label="t('CAMPAIGN.BUHOTEXT.DEVICES.CONNECT_DEVICE')"
          icon="i-lucide-plus"
          @click="handleConnectDevice"
        />
      </div>
    </div>

    <!-- Loading -->
    <div
      v-if="isFetchingDevices"
      class="flex items-center justify-center py-20"
    >
      <Spinner />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="hasNoDevices"
      class="flex flex-col items-center justify-center py-20 text-center"
    >
      <div
        class="w-24 h-24 mb-6 rounded-full bg-n-purple-3 flex items-center justify-center"
      >
        <i class="i-lucide-smartphone text-4xl text-n-purple-11" />
      </div>

      <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
        {{ t('CAMPAIGN.BUHOTEXT.DEVICES.EMPTY_STATE.TITLE') }}
      </h3>

      <p class="text-n-slate-11 mb-6 max-w-md">
        {{ t('CAMPAIGN.BUHOTEXT.DEVICES.EMPTY_STATE.SUBTITLE') }}
      </p>

      <Button
        :label="t('CAMPAIGN.BUHOTEXT.DEVICES.CONNECT_FIRST')"
        icon="i-lucide-smartphone"
        @click="handleConnectDevice"
      />
    </div>

    <!-- Devices Grid -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <BuhotextDeviceCard
        v-for="device in devices"
        :key="device.id"
        :device="device"
        @refresh="handleRefresh"
      />
    </div>

    <!-- Connect Device Dialog -->
    <BuhotextConnectDeviceDialog
      v-if="showConnectDialog"
      @close="toggleConnectDialog(false)"
      @connected="handleRefresh"
    />
  </div>
</template>
